/**
 * A file, going in.
 *
 * Decision 0017 points 4 and 5. Task 0044 found that there was no ingestion
 * anywhere — no `type="file"`, no `FileReader`, no `dataTransfer.files` — and
 * that every image, video and audio clip in the demo is a URL we typed into
 * `store-setup.ts`. This is the way in.
 *
 * ## Why this is not `attachDropHost`
 *
 * That host already exists and already listens for drops. It is for MOVING a
 * block within a lens: it reads `dataTransfer` looking for a node the page
 * itself put there, and a dropped file is not that. "A file arrived" and "a
 * block moved" are different events that happen to use the same DOM API, and
 * one host serving both would have to branch on the payload in the middle of a
 * placement decision.
 *
 * ## What it accepts, and what it does with a refusal
 *
 * Images only, for now — 0017 point 5. The still renderer exists and is
 * exercised; video and audio are the same path with a size problem attached,
 * and that problem deserves its own measurement rather than arriving on the
 * back of an image.
 *
 * A refusal SAYS so. A drop target that silently ignores what it was given is
 * indistinguishable from one that is broken, and the person is left holding a
 * file and a theory.
 */
import { blobRef, storeSource, DerivativeConflict, type BlobId } from '@morph/store';
import type { BlobStore } from './deps.js';
import { sniffedImageMime } from './image-size.js';
import {
  derivativeKeyFor, derivativeRoleFor, transformFor, TRANSFORM_VERSION, type Deriver,
} from './derivatives.js';

/** Everything about a file that survives the read. */
export interface IngestedFile {
  readonly reference: string;
  readonly mime: string;
  readonly bytes: number;
  readonly name: string;
  /** The vocabulary role this belongs under, decided at the door. */
  readonly role: string;
  /**
   * The cheap picture a card asks for, stored beside the source.
   *
   * Task 0118. `undefined` for a kind that has none — audio and PDF today — and
   * also when derivation was not asked for at all, which is the control.
   */
  readonly derived?: {
    readonly role: string;
    readonly reference: string;
    readonly mime: string;
    readonly bytes: number;
  };
  /**
   * Why there is no `derived` when there should have been one.
   *
   * A separate field from `derived` being absent, because "audio has no
   * thumbnail" and "this video would not open" are different things to tell a
   * person and one missing value cannot say which. The file is stored either
   * way — 0118 clause 6: the object still ingests with its source role, with
   * the missing derivative VISIBLE rather than silent.
   */
  readonly derivationFailure?: string;
}

export interface IngestOptions {
  readonly blobs: BlobStore;
  /** Called once per accepted file, after the bytes are durable. */
  readonly onFile: (file: IngestedFile) => void | Promise<void>;
  /** Called with a sentence a person can act on. */
  readonly onRefusal: (why: string) => void;
  /** Whether anything can receive a file right now — there has to be an object
   *  to put it on. Asked at drop time rather than at attach time, because the
   *  answer changes as a person navigates. */
  readonly canAccept: () => boolean;
}

/**
 * What a renderer here can draw, and which ROLE it belongs under.
 *
 * A list rather than `startsWith('image/')`: `image/svg+xml` is a document that
 * executes, and a drop target is exactly where one would arrive.
 *
 * The role is part of the entry because the renderer decides a medium from the
 * ROLE and never by looking inside the value — *"deciding what something is by
 * looking inside it would put content inspection in the presentation layer"*.
 * So the one place that inspects a file is here, at the door, and what it
 * produces is a vocabulary term.
 */
const ACCEPTED: ReadonlyArray<readonly [mime: string, role: string]> = [
  ['image/png',  'media.hero'], ['image/jpeg', 'media.hero'],
  ['image/gif',  'media.hero'], ['image/webp', 'media.hero'],
  ['image/avif', 'media.hero'],
  // Task 0044 step 4. The renderers have existed since decision 0014's first
  // extension; only the way in was missing.
  ['video/mp4',  'media.video'], ['video/webm', 'media.video'],
  ['audio/mpeg', 'media.audio'], ['audio/ogg',  'media.audio'],
  ['audio/wav',  'media.audio'], ['audio/mp4',  'media.audio'],
  // ACCEPTANCE.md's Fabric row asks for "everything renderable — video, audio,
  // PDF, transcript". PDF only: the renderer embeds `application/pdf` and
  // nothing else, and an accepted type whose renderer draws a blank box is
  // worse than a refusal that names what it would take.
  ['application/pdf', 'media.document'],
];

export const accepts = (mime: string): boolean => ACCEPTED.some(([m]) => m === mime);

/** Which role a file of this type belongs under, or `undefined` if none does. */
export const roleFor = (mime: string): string | undefined =>
  ACCEPTED.find(([m]) => m === mime)?.[1];

/**
 * The largest file this will take, and where the number comes from.
 *
 * `e2e/blob-scale.mjs`, on `main.77085b1c5544.js`, against **Chromium's own
 * IndexedDB behind the `0043` Worker** — which is what the harness measures now
 * and had never measured before:
 *
 * | MiB | hash ms | put ms | get ms | worst stall |
 * |---:|---:|---:|---:|---:|
 * |   1 |   5 |  18 |   7 |   2 |
 * |   4 |  15 |  37 |  21 |  14 |
 * |  16 |  61 | 148 |  68 |  71 |
 * |  64 | 253 | 613 | 289 | 310 |
 *
 * ## The table this replaces was measured against a `Map`
 *
 * Task `0142`: `e2e/blob-scale.mjs` opened the page as `?scale=1`, which
 * `store-setup.ts` uses to switch the whole page to the memory store so a
 * measurement route can never write into a person's database. Its old row for
 * 64 MB read `put 335, get 317, longest task 1213`, and none of those three
 * numbers described the shipped store.
 *
 * The direction of the correction is not the obvious one, and that is the
 * interesting part. `put` went UP — 335 to 613 — because a structured clone
 * into durable storage is real work a `Map` does not do. **The stall went
 * DOWN**, 1,213 to 310, because the old table also predates decision `0043`
 * moving hashing and cloning into a Worker. Two errors in opposite directions
 * that happened to leave the conclusion standing.
 *
 * ## What decides the number now
 *
 * **The stall is ~4.8ms of blocked main thread per MiB**, not 19. The old
 * figure was measured before the Worker existed, and the argument built on it —
 * *"a Worker would roughly HALVE the freeze"* — was answered by building one:
 * it took about a quarter, and what is left is the ingest's own repaint rather
 * than the hash.
 *
 * **64 MiB survives its own evidence**, with room: 310ms against decision
 * `0042`'s 1,824ms ceiling. It is not a number that needs defending at the
 * margin any more, which is a change in kind from the old table where 64 MB was
 * ~1.2 seconds and the ceiling was the thing standing between a person and a
 * page that had stopped.
 *
 * **It is still a choice, and still linear.** Someone could argue for 128 MiB
 * from this table — 620ms is a pause rather than a stop. What has not changed
 * is that picking one without a table is not arguable, and this is the first
 * table that measured the store the product ships.
 */
export const MAX_BYTES = 64 * 1024 * 1024;

/** Above this the block is perceptible, so the page has to say it is working
 *  BEFORE it starts — after it starts, nothing can be painted. Set at the first
 *  size that produced a long task at all. */
export const ANNOUNCE_ABOVE_BYTES = 8 * 1024 * 1024;

const mb = (bytes: number): string => `${(bytes / 1024 / 1024).toFixed(bytes > 10 * 1024 * 1024 ? 0 : 1)} MB`;

/** The `accept` attribute for a file input, from the same list — so the picker
 *  offers what the drop target takes and the two cannot drift. */
export const acceptAttribute = ACCEPTED.map(([m]) => m).join(',');

/**
 * The phases of an ingest, timed, so a red budget gate names a suspect.
 *
 * @Codex, P2 on #184: *"This records only one aggregate event-loop stall for
 * the whole attempt, even though the closed task's third acceptance clause
 * requires attribution among hashing, blob storage, derivation, object writing,
 * and repainting. When the gate turns red, its output therefore cannot
 * distinguish a hashing regression that calls for a worker from an IndexedDB
 * clone, decoder, write, or render regression, and the reported diagnosis
 * remains the same for mutually incompatible fixes."*
 *
 * `0119`'s clause 3 asked for exactly this and the task was closed without it.
 * `ingest.ts`'s own ceiling table already argues the distinction matters —
 * *"SHA-256 is roughly half of it and the structured clone out of IndexedDB is
 * most of the rest … a Worker would roughly HALVE the freeze rather than remove
 * it"* — and nobody has measured whether that still holds now that there are
 * four more steps behind the door.
 *
 * `performance.measure` rather than a callback parameter: the names are then a
 * contract any observer can read, including one that is not a test, and there
 * is no optional hook to become decorative the way `0121` records. Guarded
 * because jsdom's `performance` has no `measure` in every version, and a timing
 * facility must never be the thing that breaks an ingest.
 */
export const INGEST_PHASES = ['read', 'store-source', 'derive', 'store-derivative'] as const;
export type IngestPhase = (typeof INGEST_PHASES)[number];

async function timed<T>(phase: IngestPhase, work: () => Promise<T>): Promise<T> {
  const at = typeof performance === 'object' && typeof performance.now === 'function'
    ? performance.now() : undefined;
  try {
    return await work();
  } finally {
    if (at !== undefined && typeof performance.measure === 'function') {
      // The failure path is timed too. A decode that takes five seconds and
      // then throws is the case a person feels most, and a measure that only
      // recorded successes would be blind to exactly it.
      try { performance.measure(`morph.ingest.${phase}`, { start: at }); } catch { /* no-op */ }
    }
  }
}

/**
 * Read one file into the blob store.
 *
 * Exported for the tests and for anything that already has a `File` in hand.
 * Returns `undefined` on a refusal, having explained it.
 */
export async function ingestOne(
  file: { name: string; type: string; size?: number; arrayBuffer(): Promise<ArrayBuffer> },
  o: Pick<IngestOptions, 'blobs' | 'onRefusal'> & {
    /**
     * How to make the cheap picture. OMITTED means no derivation, which is
     * decision 0034's substitution control for every clause about one — the
     * same file through the same door with one capability withheld, rather
     * than an assertion deleted.
     */
    readonly derive?: Deriver;
    /**
     * Which contract version the deriver above implements.
     *
     * Defaults to the build's own. A caller passes one only to demonstrate
     * decision 0041's second control — that a version bump invokes the decoder
     * and creates a distinct mapping *"even when a controlled encoder returns
     * identical bytes"*, which is the case content-addressing alone gets wrong
     * and the reason the index exists at all.
     */
    readonly transformVersion?: number;
    /**
     * Which encoder the deriver above actually is, as a digest.
     *
     * Task `0129`. Passed alongside `derive` and never reached for globally,
     * for the same reason `derive` itself is a parameter: a fake deriver's
     * bytes come from the fixture, not from the browser, so a key naming the
     * build's probe while a fake produced the value is exactly the mismatched
     * row decision `0041`'s integrity rule cannot see.
     *
     * OMITTED means no key at all — the index is neither read nor written, and
     * ingestion derives. That is what an unprobed session gets, and it is the
     * safe direction: a missing encoder costs a re-derivation, a defaulted one
     * costs the rule.
     */
    readonly probe?: (transform: string, sourceMime: string) => Promise<string | undefined>;
    /**
     * Whether this ingest is still the one the person wants.
     *
     * @Codex, P2 on #184: *"When a second same-role file supersedes a still-
     * running ingest, `ingestOne()` has already persisted the older source,
     * derivative, and index mapping before this return. For unique files those
     * bytes are not shared, are never attached to any object, and no later
     * product path deletes them, so repeated replacements permanently consume
     * IndexedDB quota."*
     *
     * The supersession check lived entirely in the caller, after this returned,
     * which is the latest possible place — everything expensive and everything
     * durable had already happened. Asked here instead, before each write, so a
     * replaced ingest stops rather than finishing into a bin.
     *
     * It does not close the window completely and that is recorded in `0130`:
     * the source `put` is the first durable act and a supersession that arrives
     * during it cannot be caught before it. Whether an unattached
     * content-addressed blob should be collected at all is decision `0017`'s
     * eviction question, which it explicitly leaves open.
     *
     * Omitted means "always current", which is what every test and the
     * refusal-control path want.
     */
    readonly current?: () => boolean;
    /**
     * Somewhere to hand bytes that are already in memory.
     *
     * Called for the SOURCE, immediately after the write.
     *
     * Not for the derivative: a thumbnail is a few kilobytes, so reading it
     * back costs nothing measurable, and the whole finding is about the 64 MiB
     * source. Priming it too would mean widening this to `Uint8Array` and
     * copying into an owned buffer to satisfy `BlobPart` — a 64 MiB copy to
     * avoid a 64 MiB read. The demo primes its object-URL cache with it, which is what stops
     * the repaint reading a 64 MiB file straight back out of IndexedDB — see
     * `blob-urls.ts`.
     *
     * Optional and side-effect-only: an ingest that nobody primes behaves
     * identically, just slower, which keeps this out of the way of every test
     * that does not care.
     */
    readonly onStored?: (
      id: BlobId, bytes: Uint8Array<ArrayBuffer>, mime: string,
    ) => void;
  },
): Promise<IngestedFile | undefined> {
  /**
   * The claim, asked BEFORE the preflight refusals rather than only after the
   * read.
   *
   * @Codex, P2 on `27932c6`: *"When an oversized file A reaches the
   * announcement-frame wait in `mountIngest()` and a same-role file B
   * supersedes it during that frame, A can resume into this preflight branch
   * after B owns the claim. Because the `current()` callback is not consulted
   * until after `arrayBuffer()`, A still publishes its stale size refusal and
   * can overwrite B's success status."*
   *
   * Exact, and the window is one the guard below cannot reach because it opens
   * before this function is entered: `mountIngest` awaits a
   * `requestAnimationFrame` to announce a large read, and a person choosing a
   * second file during that frame takes the claim while A is still suspended.
   * A then arrives here with nothing awaited yet and refuses out loud.
   *
   * BOTH checks stay. They guard different windows — this one the wait before
   * the call, the one below the `arrayBuffer()` await inside it — and a single
   * check cannot cover both because there is an await between them. The rule
   * they share is `0044`'s: *"an answer about a moment that has passed must not
   * be installed over one about now"*, and a refusal is an answer.
   */
  if (o.current && !o.current()) return undefined;
  if (!accepts(file.type)) {
    // Named, not just refused. "That kind of file" is a shrug; the type and the
    // list are something a person can do something about.
    const kinds = [...new Set(ACCEPTED.map(([m]) => m.split('/')[0]))].join(', ');
    o.onRefusal(
      `${file.name || 'that file'} is ${file.type || 'of unknown type'} — `
      + `${kinds} only (${ACCEPTED.map(([m]) => m.replace(/^\w+\//, '')).join(', ')})`);
    return undefined;
  }
  if (typeof file.size === 'number' && file.size > MAX_BYTES) {
    // A size refusal names BOTH numbers. "Too big" leaves a person guessing
    // whether to try a slightly smaller file or a very different one.
    o.onRefusal(
      `${file.name || 'that file'} is ${mb(file.size)} — the limit is ${mb(MAX_BYTES)}, `
      + `because the page pauses for about 5ms per megabyte while it stores one`);
    return undefined;
  }
  let source = await timed('read',
    async () => new Uint8Array(await file.arrayBuffer()) as Uint8Array<ArrayBuffer>);
  /**
   * The ceiling again, on the bytes that ARRIVED.
   *
   * @Codex, P2 on #184: *"When a caller supplies the public `ingestOne()`
   * boundary with no `size`, a stale size, or an understated size, the
   * preflight check is skipped or passes and this line can materialize and
   * persist an arbitrarily large buffer. The function's accepted file shape
   * explicitly makes `size` optional, so the 64 MiB storage and responsiveness
   * ceiling currently depends on unverified caller metadata rather than the
   * bytes handed to the blob store."*
   *
   * `file.size` is a CLAIM. From a real `<input type=file>` it is accurate and
   * the preflight above is what a person wants — a refusal before a 200 MiB
   * read rather than after it. But `ingestOne` takes a duck-typed file whose
   * `size` this signature marks optional, and every harness and every future
   * caller that builds one is a caller whose claim nothing checked.
   *
   * So both, and neither is redundant: the preflight saves the read, and this
   * enforces the rule. Placed BEFORE `storeSource` and therefore before the
   * hash, the clone and any durable write, which is what decision `0042`
   * means by an enforced ingest ceiling rather than an advertised one.
   *
   * The wording says what was measured rather than what was declared, because
   * a person told "that file is 12 MB" about a 300 MB file learns nothing.
   */
  /**
   * CURRENCY FIRST, because a refusal is a sentence on somebody's screen.
   *
   * @Codex, P2 on `7788a4c`: *"an older same-role ingest can still be awaiting
   * `arrayBuffer()` while a newer ingest claims the slot and succeeds. If the
   * older read then exceeds `MAX_BYTES`, this branch invokes `onRefusal` before
   * the first `current()` check, so its stale 'too large' notice overwrites the
   * success status for the file the person actually chose."*
   *
   * The comment under this check used to read *"the last point before anything
   * durable happens"*, which is true and is the wrong bar. `onRefusal` writes
   * to the live region — it is not durable and it is the most visible thing
   * this function does. A superseded ingest must be silent, not merely
   * harmless.
   *
   * That is the same rule the pool door learned in `0044`: *"an answer about a
   * moment that has passed must not be installed over one about now"*, and it
   * was exempt here for the same reason it was exempt there — it leaves by a
   * different door.
   */
  if (o.current && !o.current()) return undefined;
  if (source.byteLength > MAX_BYTES) {
    o.onRefusal(
      `${file.name || 'that file'} read as ${mb(source.byteLength)} — the limit is `
      + `${mb(MAX_BYTES)}, because the page pauses for about 5ms per megabyte `
      + 'while it stores one');
    return undefined;
  }
  /**
   * LENT, not copied — decision `0043`.
   *
   * `storeSource` uses `putOwned` when the store has one, which is when the
   * source write is behind a Worker. The bytes cross by ownership transfer, so
   * `source` is DETACHED after this line and `stored.bytes` is the array to use
   * from here on. Both uses below need it: `onStored` mints an object URL from
   * it, and `derive` decodes it for a thumbnail.
   *
   * Reassigned rather than shadowed, so there is no way to reach the detached
   * one by mistake. A backend without a worker hands back the same array it was
   * given, so this is free there.
   */
  const written = await timed('store-source', () => storeSource(o.blobs, source, file.type));
  const ref = written.ref;
  source = written.bytes;
  // Before the currency check below: the bytes are durable, so a URL minted
  // from them is correct whether or not this ingest is still the current one.
  /**
   * ISOLATED. @Codex, P2 on #184: *"When `onStored` throws — for example, if
   * creating the 64 MiB source Blob or its object URL fails under memory
   * pressure — the exception escapes after the source has become durable, so
   * `ingestOne()` never returns an `IngestedFile` and `attachFile()` never
   * runs. This optional cache-priming optimization can therefore make the
   * person's file fail and leave its source orphaned."*
   *
   * Right, and it is the worst place for it: the bytes are already durable, so
   * the failure costs the attach without saving the store. An optimisation that
   * can lose a file is not one. Swallowed rather than reported, because there
   * is nothing for a person to do about a warm cache — the next repaint reads
   * the blob back and everything works, slowly.
   */
  try {
    o.onStored?.(ref.id, source, ref.mime);
  } catch { /* a cold cache is slower, not broken */ }
  /**
   * Again, immediately. @Codex, P2 on #184: *"When a replacement arrives while
   * the older ingest is awaiting `blobs.put()` — for example during
   * asynchronous hashing — the pre-write `current()` check has already passed.
   * After this await, the superseded ingest still performs the index lookup and
   * a full image or video decode before checking again … so a discarded 64 MP
   * image or slow video can continue consuming memory and blocking the page for
   * seconds."*
   *
   * The source is durable by now and `0130` records why that is not simply
   * deleted. The decode is not durable and is the expensive part, so stopping
   * here costs the person nothing and saves them the whole of it.
   */
  if (o.current && !o.current()) return undefined;
  /**
   * The role comes from the REFERENCE's type, not from `file.type`.
   *
   * Decision `0047`: *"The ingestion control must select the attachment role
   * from the same canonical media type carried by the returned typed reference.
   * It may not select a role from `file.type` and then attach a reference whose
   * `mime` differs."*
   *
   * Today those two strings agree, because a typed reference records the type
   * it was accepted under and `put` is handed `file.type`. That is exactly why
   * the rule is worth obeying while it costs nothing: under the first-wins
   * store `0047` replaced, the same bytes offered as `video/mp4` after
   * `audio/mp4` came back as audio, and reading `file.type` here produced an
   * attachment whose role said video and whose `mime` said audio — one object,
   * two answers, and the renderer picks the medium from the role.
   *
   * Every use below moves with it. `derive`, the transform key and the
   * derivative-role probe all decide how to DECODE these bytes, and the
   * reference's type is the store's answer to what they are; `file.type` is the
   * browser's guess about a file that has since been accepted under a name of
   * its own.
   */
  const accepted = ref.mime;
  const role = roleFor(accepted);
  if (role === undefined) {
    /**
     * The store accepted these bytes under a type this door cannot draw.
     *
     * Unreachable while `put` records the type it is given, and it is not
     * decoration: it is the difference between a refusal that names the
     * disagreement and `roleFor(...)!` attaching an object with `undefined` as
     * its role — which the renderer would meet as a medium it has no case for,
     * one repaint away from the door that could have said so.
     *
     * The source stays durable. `0130` records why an orphan is cheaper than a
     * delete on a path that can race a replacement.
     */
    o.onRefusal(
      `${file.name || 'that file'} was stored as ${accepted}, which is not a kind `
      + `this can show — it was offered as ${file.type || 'an unknown type'}`);
    return undefined;
  }
  const stored: IngestedFile = {
    reference: blobRef(ref), mime: ref.mime, bytes: ref.bytes,
    name: file.name || 'file', role,
  };
  if (!o.derive) return stored;

  /**
   * Before decoding, not after.
   *
   * Decision 0041: *"ingestion looks up this key before decoding, reuses a
   * completed entry when its blob still exists, and otherwise derives."* The
   * comment this replaces argued that content addressing already made the
   * second ingest cheap, and it was wrong about which cost mattered: `put` is
   * idempotent, so the second copy is free, but you only learn the derivative's
   * id BY decoding the source to compute it. The decode is the expense, and it
   * was being paid every time.
   *
   * A hit whose blob is gone is not a hit — `lookup` checks presence, so the
   * stale case arrives here as `undefined` and falls through to derivation,
   * which is the repair.
   */
  /**
   * The encoder BEFORE the key, and outside every `timed` phase.
   *
   * `encoderProbe` is memoised for the life of the page and `main.ts` warms it
   * at load, so this await is normally already settled. It is still an await
   * rather than an assumption: a drop in the first moments of a session is a
   * real ordering, and so is a browser whose encoder never answers — the probe
   * carries the same five-second deadline every other decode in this build
   * does, and waiting it out here is the honest cost of a key that names what
   * ran. A key built from a pending promise would be a key built from
   * `undefined`, which is the shared row this parameter exists to end.
   *
   * A probe that fails hands back `undefined`, which `derivativeKeyFor`
   * refuses, and the `if (key)` guards below already mean "derive, do not
   * consult the index" — so the failure path needed no new branch.
   */
  const transform = transformFor(accepted);
  // Asked PER TRANSFORM, because a build can identify one pipeline and not
  // another: the still probe decodes a PNG through `createImageBitmap` and
  // never touches the `<video>` decoder a poster frame goes through. @Codex,
  // P2 on #184. An answer of `undefined` means no key and therefore no index,
  // which is the same road an unprobed session already takes.
  // The SOURCE type as well as the transform. @Codex, P2 on `857dbfa`: a
  // PNG-only probe answered the same digest for all five accepted image
  // formats, so a JPEG or AVIF decoder change was invisible to the key —
  // decision 0041 puts `sourceMime` in it for exactly the reason the probe
  // was ignoring.
  const encoder = o.probe && transform ? await o.probe(transform, accepted) : undefined;
  /**
   * And ASKED AGAIN after it, because that await can be five seconds long.
   *
   * @Codex, P2 on `dcc6189`: *"On the first image ingest, `o.probe(transform)`
   * can remain pending until its five-second deadline; if a newer same-role
   * file supersedes this ingest during that await, the stale call proceeds
   * through the derivative-index lookup and a full image decode without
   * consulting `current()` again. The next check is only after derivation …
   * an abandoned large image can consume substantial decoded memory and
   * several seconds of work while contending with the replacement."*
   *
   * Right, and it is this morning's own await. The paragraph above argues that
   * waiting the probe out is *"the honest cost of a key that names what ran"*
   * — true of an ingest somebody still wants, and the check that would have
   * noticed the difference sat two lines above the new wait rather than below
   * it. Every OTHER long await in this function already has one under it;
   * this one arrived after the pattern and did not inherit it.
   *
   * Before the lookup as well as before the decode, which the review asks for
   * separately: a lookup is a round trip to IndexedDB, and a superseded ingest
   * has no use for its answer either.
   */
  if (o.current && !o.current()) return undefined;
  /**
   * KEY WHAT DECODES, not what the label says — @Codex, P2 on `40b56c2`.
   * `createImageBitmap` sniffs, so AVIF bytes accepted as JPEG decode
   * through the AVIF pipeline while this key carried the JPEG probe: an
   * AVIF decoder change could then reuse a stale derivative under an
   * unchanged key, or raise a false integrity conflict. A file whose
   * declared and sniffed formats disagree simply skips the index — no key,
   * derive fresh, exactly the road an unprobed session takes — because a
   * mislabelled file is rare and a wrong cache hit is a wrong picture.
   */
  const sniffed = accepted.startsWith('image/') ? sniffedImageMime(source) : undefined;
  const mislabelled = sniffed !== undefined && sniffed !== accepted;
  const key = mislabelled ? undefined : derivativeKeyFor(
    ref.id, accepted, encoder, o.transformVersion ?? TRANSFORM_VERSION);
  if (key) {
    /**
     * A lookup that FAILS is a miss, not a failed ingest.
     *
     * @Codex, P2 on #184: *"If this cache lookup rejects — for example, because
     * its IndexedDB transaction aborts — the error occurs after the source blob
     * is durable but outside the derivation guard below. `ingestOne()`
     * therefore rejects, `attachFile()` is never called, and the source remains
     * orphaned with an unhandled error, even though cache reuse is optional
     * preview work."*
     *
     * Right, and it is clause 6 escaping through a door I opened: the index is
     * an OPTIMISATION, so nothing about it may cost a person their file. A
     * broken index means deriving again, which is the same thing a miss means.
     */
    let held;
    let at;
    try {
      held = await o.blobs.derivatives.lookup(key);
      /**
       * `stat` on the TYPED PAIR, not on the bare digest.
       *
       * For the length, which the mapping deliberately does not carry: a byte
       * count in the index is a second copy of something `blob-meta` already
       * knows, and two records of one fact are two that can disagree.
       *
       * @Codex, P2 on `250d2f9`: *"When a stale mapping's derived digest is
       * deleted and identical bytes are later accepted only under another MIME,
       * `derivatives.lookup()` treats the digest as present and this bare
       * `stat()` resolves through that other typed row. The code then emits a
       * reference using `held.mime`, whose exact `(id, mime)` row does not
       * exist, so rendering loses the preview while ingestion incorrectly skips
       * re-derivation."*
       *
       * Right, and it is `0047`'s own rule arriving at a caller written before
       * it: *"APIs that resolve a person's reference (`get` and `stat`) must
       * take the typed reference identity, not silently discard its media type
       * and look up by digest alone."* The cache hit is then a claim about a
       * row that exists, and a mapping whose type is gone falls through to
       * deriving again — which is what a miss means and what the repair is.
       */
      at = held ? await o.blobs.stat({ id: held.derived, mime: held.mime }) : undefined;
    } catch { held = undefined; at = undefined; }
    if (held && at) {
      return {
        ...stored,
        derived: {
          role: held.role, reference: blobRef({ id: held.derived, mime: held.mime }),
          mime: held.mime, bytes: at.bytes,
        },
      };
    }
  }

  /**
   * And AGAIN after the index, which is the half the comment above already
   * claimed and the code did not have.
   *
   * @Codex, P2 on `669e5dd`: *"When a same-role replacement arrives while
   * `derivatives.lookup()` or the subsequent `stat()` is awaiting IndexedDB, a
   * cache miss falls through directly to `o.derive(...)` without consulting
   * `current()` again, so a superseded large image or video can still consume
   * substantial decoding memory and seconds of work."*
   *
   * The check above says *"Before the lookup as well as before the decode,
   * which the review asks for separately"* — and then there is one check, above
   * the lookup. A comment describing a guard the code does not have is the
   * failure this repository keeps naming, committed by the paragraph that
   * names it.
   *
   * Two awaits sit between them, both round trips to IndexedDB, and a decode is
   * the longest phase in this function. Cheap to ask, and the answer is the
   * difference between a wasted second and a wasted minute.
   */
  if (o.current && !o.current()) return undefined;

  /**
   * The source is durable BEFORE this runs, and stays durable if it throws.
   *
   * 0118 clause 6. A decode failure must cost the picture and nothing else —
   * put the derivative first and a browser that will not open one video codec
   * turns into a door that refuses the file.
   */
  /**
   * The decode AND the store of what it produced, both inside the guard.
   *
   * @Codex, P2 on #184: *"If the source blob fits but the subsequent derivative
   * write fails, for example because IndexedDB reaches quota or aborts its
   * transaction, this rejection bypasses the decode-failure handling above.
   * `ingestOne()` rejects, the event handler never calls `attachFile()`, and
   * the user gets an unhandled error while the already stored source remains
   * orphaned."*
   *
   * Correct, and the shape is the one clause 6 exists to prevent, one step
   * later than I guarded it: a preview that cannot be stored must cost the
   * preview and not the file.
   */
  let derived;
  try {
    const derivative = await timed('derive', () => o.derive!(source, accepted));
    if (!derivative) {
      return derivativeRoleFor(accepted)
        ? { ...stored, derivationFailure: 'the decoder returned no picture' }
        : stored;
    }
    // Asked again. The decode is the long phase — seconds for a large picture —
    // so this is where a replacement almost always arrives, and it is still
    // before the derivative and its mapping become durable.
    if (o.current && !o.current()) return undefined;
    const at = await timed('store-derivative',
      () => o.blobs.put(derivative.bytes, derivative.mime));
    derived = { at, role: derivative.role };
    // Published only once both blobs are durable, and inside the same guard.
    // A mapping written before its blob is a mapping that promises bytes that
    // may never arrive, and decision 0041 is explicit that a failed derivative
    // write *"must not publish a completed mapping"*.
    //
    /**
     * The INDEX is an optimisation; the derivative is the product.
     *
     * @Codex, P2 on `669e5dd`: *"When the derivative blob is stored
     * successfully but `derivatives.publish()` rejects because of a transient
     * IndexedDB transaction failure rather than a `DerivativeConflict`, the
     * shared catch reports a derivation failure and omits the already valid
     * derivative from the attachment. The index is only an optimization for
     * avoiding later decodes, so its storage failure should not remove the
     * current thumbnail or poster."*
     *
     * Right, and it is the same rule as the two above it read one layer in.
     * `0118` clause 6 says a decode failure costs the picture and not the file;
     * this says a MAPPING failure costs the next ingest's shortcut and not this
     * ingest's picture. The bytes are durable and correct either way — all that
     * is lost is that somebody decodes again later.
     *
     * A `DerivativeConflict` is the exception and stays one, because `0041` is
     * explicit that *"conflicting completed mappings are integrity errors, not
     * last-writer-wins"*. That is a real disagreement about what these bytes
     * are and it is worth a visible sentence; a transaction that aborted is
     * not.
     */
    if (key) {
      try {
        await o.blobs.derivatives.publish(key, {
          derived: at.id, role: derivative.role, mime: at.mime,
          width: derivative.width, height: derivative.height,
          ...(derivative.duration === undefined ? {} : { duration: derivative.duration }),
        });
      } catch (error) {
        if (error instanceof DerivativeConflict) throw error;
      }
    }
  } catch (error) {
    return {
      ...stored,
      derivationFailure: error instanceof Error ? error.message : String(error),
    };
  }
  return {
    ...stored,
    derived: {
      role: derived.role,
      reference: blobRef(derived.at),
      mime: derived.at.mime,
      bytes: derived.at.bytes,
    },
  };
}
